import { useState, useEffect, useCallback } from "react";
import { useAdminApi } from "../adminUtils";
import { useStudio } from "../../../studios/StudioContext";
import ImageField from "../../../components/ImageField";
import { resolveImg } from "../../../components/calendarUtils";
import CouponsTab from "./CouponsTab";
import SignaturesSection from "./SignaturesSection";
import TeamSection from "./TeamSection";
import "../admin-base.css";
import "../admin-layout.css";
import "../admin-controls.css";
import "../admin-students.css";
import "../admin-misc.css";
import "../admin-responsive.css";

function TeacherEditor({ initial, onCancel, onSaved }) {
  const { API, AUTH } = useAdminApi();
  const isNew = !initial?._id;
  const [form, setForm]     = useState({ name: "", email: "", bio: "", image: "", ...initial, payRate: initial?.payRate ?? "" });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) { setError("Name and email are required."); return; }
    setSaving(true); setError(null);
    try {
      const body = { name: form.name.trim(), email: form.email.trim(), payRate: Number(form.payRate) || 0,
                     bio: (form.bio || "").trim(), image: form.image || "" };
      const res = await fetch(isNew ? `${API}/teachers` : `${API}/teachers/${initial._id}`, {
        method: isNew ? "POST" : "PATCH", headers: AUTH, body: JSON.stringify(body),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setError(d.error ?? "Failed."); return; }
      onSaved();
    } catch { setError("Something went wrong."); }
    finally   { setSaving(false); }
  };

  return (
    <div className="adm-teacher-editor">
      <div className="adm-form-grid" style={{ maxWidth: "560px" }}>
        <label className="adm-form-label">Name <span className="adm-form-hint">(as it appears on classes)</span>
          <input className="adm-input" value={form.name} onChange={set("name")} placeholder="e.g. Dominique" />
        </label>
        <label className="adm-form-label">Email
          <input className="adm-input" type="email" value={form.email} onChange={set("email")} placeholder="teacher@email.com" />
        </label>
        <label className="adm-form-label">Pay rate ($/student)
          <input className="adm-input" type="number" min="0" step="0.01" value={form.payRate} onChange={set("payRate")} placeholder="0.00" />
        </label>
      </div>
      <label className="adm-form-label" style={{ display: "block", maxWidth: "560px", marginTop: "0.75rem" }}>Bio
        <textarea className="adm-input" rows={5} value={form.bio || ""} onChange={set("bio")} placeholder="Shown when students tap “Show bio”" />
      </label>
      <ImageField value={form.image || ""} onChange={v => setForm(f => ({ ...f, image: v }))} label="Photo" />
      {error && <p className="adm-error">{error}</p>}
      <div className="adm-modal-actions" style={{ justifyContent: "flex-start", marginTop: "0.75rem" }}>
        <button className="adm-btn adm-btn--ghost" onClick={onCancel}>Cancel</button>
        <button className="adm-btn adm-btn--primary" onClick={save} disabled={saving}>
          {saving ? "Saving…" : isNew ? "Add Teacher" : "Save Teacher"}
        </button>
      </div>
    </div>
  );
}

function TeachersSection() {
  const { API, AUTH } = useAdminApi();
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [editing, setEditing]   = useState(null);   // null | "new" | teacher _id

  const fetchTeachers = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/teachers`, { headers: AUTH });
      const data = await res.json();
      setTeachers(data.teachers ?? []);
    } catch { setTeachers([]); }
    finally   { setLoading(false); }
  }, [API, AUTH]);

  useEffect(() => { fetchTeachers(); }, [fetchTeachers]);

  const handleRemove = async id => {
    if (!window.confirm("Remove this teacher?")) return;
    await fetch(`${API}/teachers/${id}`, { method: "DELETE", headers: AUTH });
    fetchTeachers();
  };
  const done = () => { setEditing(null); fetchTeachers(); };

  return (
    <div className="adm-settings-section">
      <h3 className="adm-settings-heading">Teachers</h3>
      {loading && <p className="adm-loading">Loading…</p>}
      {!loading && (
        <div className="adm-teacher-list">
          {teachers.map(t => editing === t._id
            ? <TeacherEditor key={t._id} initial={t} onCancel={() => setEditing(null)} onSaved={done} />
            : (
              <div key={t._id} className="adm-teacher-row">
                {t.image
                  ? <img className="adm-teacher-thumb" src={resolveImg(API, t.image)} alt="" />
                  : <span className="adm-teacher-thumb adm-teacher-thumb--empty">{(t.name || "?")[0]}</span>}
                <div className="adm-teacher-info">
                  <strong>{t.name}</strong>
                  <span>{t.email} · ${t.payRate}/student{t.bio ? "" : " · no bio yet"}</span>
                </div>
                <button className="adm-btn-ghost-sm" onClick={() => setEditing(t._id)}>Edit</button>
                <button className="adm-email-remove" onClick={() => handleRemove(t._id)} aria-label={`Remove ${t.name}`}>✕</button>
              </div>
            ))}
        </div>
      )}
      {editing === "new"
        ? <TeacherEditor initial={{}} onCancel={() => setEditing(null)} onSaved={done} />
        : (
          <button className="adm-btn adm-btn--sm" style={{ marginTop: "0.75rem" }} onClick={() => setEditing("new")}>
            + Add Teacher
          </button>
        )}
    </div>
  );
}

function VenuesSection() {
  const { API, AUTH } = useAdminApi();
  const [venues, setVenues]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm]           = useState({ name: "", address: "", mapsLink: "", capacity: "" });
  const [error, setError]         = useState(null);
  const [saving, setSaving]       = useState(false);

  const fetchVenues = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/venues`, { headers: AUTH });
      const data = await res.json();
      setVenues(data.venues ?? []);
    } catch { setVenues([]); }
    finally   { setLoading(false); }
  }, [API, AUTH]);

  useEffect(() => { fetchVenues(); }, [fetchVenues]);

  const resetForm = () => {
    setForm({ name: "", address: "", mapsLink: "", capacity: "" });
    setEditingId(null);
    setShowForm(false);
    setError(null);
  };

  const handleEdit = (v) => {
    setForm({ name: v.name, address: v.address ?? "", mapsLink: v.mapsLink ?? "", capacity: v.capacity ?? "" });
    setEditingId(v.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError("Name is required."); return; }
    setSaving(true); setError(null);
    try {
      const body = {
        name:     form.name.trim(),
        address:  form.address.trim() || null,
        mapsLink: form.mapsLink.trim() || null,
        capacity: form.capacity ? Number(form.capacity) : null,
      };
      let res;
      if (editingId) {
        res = await fetch(`${API}/venues/${editingId}`, { method: "PATCH", headers: AUTH, body: JSON.stringify(body) });
      } else {
        res = await fetch(`${API}/venues`, { method: "POST", headers: AUTH, body: JSON.stringify(body) });
      }
      if (!res.ok) { const d = await res.json(); setError(d.error ?? "Failed."); return; }
      resetForm();
      fetchVenues();
    } catch { setError("Something went wrong."); }
    finally   { setSaving(false); }
  };

  const handleRemove = async (id) => {
    if (!window.confirm("Deactivate this venue?")) return;
    await fetch(`${API}/venues/${id}`, { method: "DELETE", headers: AUTH });
    fetchVenues();
  };

  return (
    <div className="adm-settings-section">
      <h3 className="adm-settings-heading">Venues</h3>
      {loading && <p className="adm-loading">Loading…</p>}
      {!loading && (
        <div className="adm-email-list">
          {venues.map(v => (
            <div key={v.id} className="adm-email-chip">
              <span><strong>{v.name}</strong>{v.address ? ` — ${v.address}` : ""}</span>
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <button className="adm-btn adm-btn--sm" onClick={() => handleEdit(v)}>Edit</button>
                {v.id !== "venue_online" && (
                  <button className="adm-email-remove" onClick={() => handleRemove(v.id)}>x</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {!showForm && (
        <button className="adm-btn adm-btn--sm" style={{ marginTop: "0.75rem" }} onClick={() => setShowForm(true)}>
          + Add Venue
        </button>
      )}
      {showForm && (
        <div style={{ marginTop: "1rem" }}>
          <div className="adm-form-grid" style={{ maxWidth: "480px" }}>
            <label className="adm-form-label">Name
              <input className="adm-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Venue name" />
            </label>
            <label className="adm-form-label">Address
              <input className="adm-input" value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="123 Main St, City, FL 00000" />
            </label>
            <label className="adm-form-label">Google Maps link
              <input className="adm-input" value={form.mapsLink} onChange={e => setForm(f => ({ ...f, mapsLink: e.target.value }))} placeholder="https://maps.google.com/..." />
            </label>
            <label className="adm-form-label">Capacity
              <input className="adm-input" type="number" min="1" value={form.capacity} onChange={e => setForm(f => ({ ...f, capacity: e.target.value }))} placeholder="Leave blank for no limit" />
            </label>
          </div>
          {error && <p className="adm-error">{error}</p>}
          <div className="adm-modal-actions" style={{ justifyContent: "flex-start", marginTop: "0.75rem" }}>
            <button className="adm-btn adm-btn--ghost" onClick={resetForm}>Cancel</button>
            <button className="adm-btn adm-btn--primary" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : editingId ? "Save Changes" : "Add Venue"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SettingsTab() {
  const { API, AUTH } = useAdminApi();
  const { studioId } = useStudio();
  const [settings, setSettings]   = useState(null);
  const [loading, setLoading]     = useState(true);
  const [saved, setSaved]         = useState(false);
  const [newEmail, setNewEmail]   = useState("");

  const [loadError, setLoadError] = useState(false);
  const [saveError, setSaveError] = useState(false);

  useEffect(() => {
    setLoadError(false);
    fetch(`${API}/settings?studio=${studioId}`, { headers: AUTH })
      .then(r => r.json())
      .then(d => setSettings(d.settings ?? d))
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [API, AUTH]);

  const save = async (patch) => {
    setSaved(false); setSaveError(false);
    try {
      const res = await fetch(`${API}/settings?studio=${studioId}`, {
        method: "PATCH", headers: AUTH,
        body: JSON.stringify(patch),
      });
      const d = await res.json();
      setSettings(d.settings ?? d);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch { setSaveError(true); }
  };

  const addEmail = () => {
    if (!newEmail.trim() || settings.adminEmails.includes(newEmail.trim())) return;
    const updated = [...settings.adminEmails, newEmail.trim()];
    setSettings(s => ({ ...s, adminEmails: updated }));
    save({ adminEmails: updated });
    setNewEmail("");
  };

  const removeEmail = email => {
    const updated = settings.adminEmails.filter(e => e !== email);
    setSettings(s => ({ ...s, adminEmails: updated }));
    save({ adminEmails: updated });
  };

  const TYPE_PALETTE = ["#7A4E9C", "#2E8B7A", "#D9822B", "#3C6E9F", "#B5485A", "#6B8E23"];
  const classTypes = settings?.classTypes ?? [];
  const setClassTypes = next => setSettings(s => ({ ...s, classTypes: next }));
  const updateType = (i, key, val) => setClassTypes(classTypes.map((t, j) => j === i ? { ...t, [key]: val } : t));
  const addType = () => setClassTypes([...classTypes, { name: "", color: TYPE_PALETTE[classTypes.length % TYPE_PALETTE.length] }]);
  const removeType = i => setClassTypes(classTypes.filter((_, j) => j !== i));

  if (loading) return <div className="adm-tab-content"><p className="adm-loading">Loading…</p></div>;

  return (
    <div className="adm-tab-content">
      <h2 style={{ fontSize: "1.1rem", fontWeight: 700, marginBottom: "1.5rem", color: "var(--color-grey-600)" }}>Settings</h2>

      {loadError && <p className="adm-error">Couldn't load settings — check your connection and refresh.</p>}

      <div className="adm-settings-grid">

        {/* ── Left column ── */}
        <div className="adm-settings-grid-lhs">

          <div className="adm-settings-section">
            <h3 className="adm-settings-heading">Announcement Banner</h3>
            <div className="adm-settings-row adm-settings-row--inline">
              <label className="adm-settings-label">Show banner to students</label>
              <input
                type="checkbox"
                checked={!!settings.announcementActive}
                onChange={e => {
                  const updated = { ...settings, announcementActive: e.target.checked };
                  setSettings(updated);
                  save({ announcementActive: e.target.checked });
                }}
              />
            </div>
            <div className="adm-settings-row">
              <label className="adm-settings-label">Announcement text</label>
              <input
                type="text"
                className="adm-input"
                placeholder="e.g. Studio closed 4th July — no classes"
                style={{ maxWidth: 480 }}
                value={settings.announcementText ?? ""}
                onChange={e => setSettings(s => ({ ...s, announcementText: e.target.value }))}
                onBlur={() => save({ announcementText: settings.announcementText })}
              />
            </div>
            <div className="adm-settings-row">
              <label className="adm-settings-label">Link URL (optional)</label>
              <input
                type="text"
                className="adm-input"
                placeholder="https://…"
                style={{ maxWidth: 480 }}
                value={settings.announcementUrl ?? ""}
                onChange={e => setSettings(s => ({ ...s, announcementUrl: e.target.value }))}
                onBlur={() => save({ announcementUrl: settings.announcementUrl })}
              />
            </div>
            <div className="adm-settings-row">
              <label className="adm-settings-label">Link text</label>
              <input
                type="text"
                className="adm-input"
                placeholder="Learn more"
                style={{ maxWidth: 480 }}
                value={settings.announcementLinkText ?? ""}
                onChange={e => setSettings(s => ({ ...s, announcementLinkText: e.target.value }))}
                onBlur={() => save({ announcementLinkText: settings.announcementLinkText })}
              />
            </div>
          </div>

          <div className="adm-settings-section">
            <h3 className="adm-settings-heading">Class Types</h3>
            <p className="adm-classtype-hint">Classes whose subject contains a type's name get its color. Use / for alternates, e.g. Vinyasa/Flow.</p>
            {classTypes.map((t, i) => (
              <div className="adm-classtype-row" key={i}>
                <input type="color" className="adm-classtype-color" aria-label="Color" value={t.color}
                  onChange={e => updateType(i, "color", e.target.value)} />
                <input className="adm-input" placeholder="e.g. Hatha" value={t.name}
                  onChange={e => updateType(i, "name", e.target.value)} />
                <button type="button" className="adm-btn-ghost-sm" aria-label="Remove" onClick={() => removeType(i)}>✕</button>
              </div>
            ))}
            <div className="adm-classtype-actions">
              <button type="button" className="adm-btn-ghost-sm" onClick={addType}>+ Add type</button>
              <button type="button" className="adm-btn adm-btn--primary" onClick={() => save({ classTypes })}>Save class types</button>
            </div>
          </div>

          <div className="adm-settings-section">
            <h3 className="adm-settings-heading">Notifications</h3>
            <div className="adm-settings-row">
              <label className="adm-settings-label">Admin notifications</label>
              <div className="adm-radio-group">
                <label className="adm-radio-label">
                  <input type="radio" name="notifMode" value="on"
                    checked={settings?.notificationMode !== "off"}
                    onChange={() => save({ notificationMode: "on" })} />
                  On — 8pm evening digest + 30 min pre-class reminder
                </label>
                <label className="adm-radio-label">
                  <input type="radio" name="notifMode" value="off"
                    checked={settings?.notificationMode === "off"}
                    onChange={() => save({ notificationMode: "off" })} />
                  Off
                </label>
              </div>
            </div>
            <div className="adm-settings-row">
              <label className="adm-settings-label">Notification recipients</label>
              <div className="adm-email-list">
                {settings?.adminEmails?.map(e => (
                  <div key={e} className="adm-email-chip">
                    <span>{e}</span>
                    <button className="adm-email-remove" onClick={() => removeEmail(e)}>✕</button>
                  </div>
                ))}
                <div className="adm-email-add-row">
                  <input className="adm-input" placeholder="Add email address" value={newEmail}
                    onChange={e => setNewEmail(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && addEmail()} />
                  <button className="adm-btn adm-btn--primary" onClick={addEmail}>Add</button>
                </div>
              </div>
              {saved && <p className="adm-saved">✓ Saved</p>}
              {saveError && <p className="adm-error">Save failed — your change was NOT stored. Try again.</p>}
            </div>
          </div>

          <VenuesSection />

        </div>

        {/* ── Right column ── */}
        <div className="adm-settings-grid-rhs">

          <div className="adm-settings-section">
            <h3 className="adm-settings-heading">Signatures</h3>
            <SignaturesSection />
          </div>

          <div className="adm-settings-section">
            <h3 className="adm-settings-heading">Coupons</h3>
            <CouponsTab />
          </div>

          <div className="adm-settings-section">
            <h3 className="adm-settings-heading">Class Capacity Defaults</h3>
            <div className="adm-settings-row">
              <label className="adm-settings-label">Default cap (all classes)</label>
              <input
                type="number"
                className="adm-input"
                placeholder="No default"
                min="1"
                style={{ maxWidth: 120 }}
                value={settings.defaultCap ?? ''}
                onChange={e => setSettings(s => ({ ...s, defaultCap: e.target.value ? Number(e.target.value) : null }))}
                onBlur={() => save({ defaultCap: settings.defaultCap })}
              />
            </div>
            <div className="adm-settings-row adm-settings-row--inline">
              <label className="adm-settings-label">Enable waitlist by default</label>
              <input
                type="checkbox"
                checked={!!settings.defaultWaitlistEnabled}
                disabled={!settings.defaultCap}
                onChange={e => {
                  const updated = { ...settings, defaultWaitlistEnabled: e.target.checked };
                  setSettings(updated);
                  save({ defaultWaitlistEnabled: e.target.checked });
                }}
              />
            </div>
            <div className="adm-settings-row">
              <label className="adm-settings-label" htmlFor="wl-hold">Hold a waitlist spot for (hours)</label>
              <input
                id="wl-hold"
                type="number"
                className="adm-input"
                min="1"
                max="72"
                style={{ maxWidth: 120 }}
                value={settings.waitlistHoldHours ?? 12}
                onChange={e => setSettings(s => ({ ...s, waitlistHoldHours: e.target.value ? Number(e.target.value) : "" }))}
                onBlur={() => {
                  const h = Math.min(72, Math.max(1, Math.round(Number(settings.waitlistHoldHours) || 12)));
                  setSettings(s => ({ ...s, waitlistHoldHours: h }));
                  save({ waitlistHoldHours: h });
                }}
              />
              <p className="adm-settings-hint">When a spot opens, the first person on the waitlist is emailed and the spot is kept for them this long (never past the start of class). If they don't confirm, it goes to the next person.</p>
            </div>
          </div>

          <TeachersSection />

          <TeamSection />

        </div>

      </div>
    </div>
  );
}